import { useState, useEffect } from 'react';
import * as authApi from '../api/authClient';
import { IS_CAPACITOR } from '../api/config';
import '../styles/invite.css';

function InviteLinkModal({ onClose }) {
  const [inviteUrl, setInviteUrl] = useState('');
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    authApi.getInvites()
      .then((res) => res.json())
      .then((data) => {
        if (data.invites) setInvites(data.invites);
      })
      .catch(() => {});
  }, []);

  const handleCreate = async () => {
    setError('');
    setLoading(true);
    setCopied(false);
    try {
      const res = await authApi.createInvite();
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create invite');
      const origin = IS_CAPACITOR
        ? 'https://stellarmusic.io'
        : window.location.origin.replace('stellarmusic.xyz', 'stellarmusic.io');
      setInviteUrl(`${origin}/invite/${data.code}`);
      setInvites((prev) => [
        { code: data.code, status: 'pending', createdAt: new Date().toISOString(), acceptedBy: null },
        ...prev,
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = inviteUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: 'Join me on Stellar',
        text: 'Check out Stellar — explore music in a whole new way!',
        url: inviteUrl,
      });
    } catch {}
  };

  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleDelete = async (code) => {
    try {
      const res = await authApi.deleteInvite(code);
      if (res.ok) {
        setInvites((prev) => prev.filter((inv) => inv.code !== code));
        if (inviteUrl.includes(code)) setInviteUrl('');
      }
    } catch {}
  };

  const pendingInvites = invites.filter((inv) => inv.status === 'pending');
  const acceptedInvites = invites.filter((inv) => inv.status === 'accepted');

  return (
    <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="invite-modal">
        <button className="auth-modal-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className="invite-modal-title">Invite Friends</h2>
        <p className="invite-modal-desc">
          Generate a link to share with friends. When they sign up, you'll automatically become friends.
        </p>

        {error && <div className="invite-error">{error}</div>}

        {inviteUrl ? (
          <div className="invite-link-box">
            <input
              className="invite-link-input"
              value={inviteUrl}
              readOnly
              onClick={(e) => e.target.select()}
            />
            {canShare ? (
              <button className="invite-copy-btn invite-share-btn" onClick={handleShare}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Share
              </button>
            ) : null}
            <button className="invite-copy-btn" onClick={handleCopy}>
              {copied ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              )}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        ) : (
          <button
            className="invite-generate-btn"
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate invite link'}
          </button>
        )}

        {inviteUrl && (
          <button
            className="invite-new-link-btn"
            onClick={() => { setInviteUrl(''); setCopied(false); }}
          >
            + Generate another link
          </button>
        )}

        {(pendingInvites.length > 0 || acceptedInvites.length > 0) && (
          <div className="invite-history">
            <h3 className="invite-history-title">Your invites</h3>
            <div className="invite-history-list">
              {pendingInvites.map((inv) => (
                <div key={inv.code} className="invite-history-item">
                  <div className="invite-history-info">
                    <span className="invite-history-code">...{inv.code.slice(-4)}</span>
                    <span className="invite-history-status pending">Pending</span>
                  </div>
                  <button
                    className="invite-history-delete"
                    onClick={() => handleDelete(inv.code)}
                    title="Delete invite"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
              {acceptedInvites.map((inv) => (
                <div key={inv.code} className="invite-history-item">
                  <div className="invite-history-info">
                    <span className="invite-history-accepted-name">
                      {inv.acceptedBy ? inv.acceptedBy.displayName : 'Someone'}
                    </span>
                    <span className="invite-history-status accepted">Joined</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InviteLinkModal;
